import { useMemo } from "react";
import { useFeed } from "../hooks/useFeed";
import { PanelHeader } from "./PanelHeader";
import type { DistrictStats } from "@kmitl/shared";

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";

// ── chart helpers ────────────────────────────────────────────────────────

function fmt(n: number): string {
  return n.toLocaleString("en-US");
}
function pct(n: number, total: number): string {
  if (total <= 0) return "0%";
  return `${((n / total) * 100).toFixed(1)}%`;
}

// Build an SVG arc path for one slice of a donut. Returns the `d` attribute
// for a closed shape covering the slice from startAngle to endAngle (radians)
// around (cx,cy) with outer radius R and inner radius r.
function donutSlice(cx: number, cy: number, R: number, r: number, start: number, end: number): string {
  const large = end - start > Math.PI ? 1 : 0;
  const x1 = cx + R * Math.sin(start);
  const y1 = cy - R * Math.cos(start);
  const x2 = cx + R * Math.sin(end);
  const y2 = cy - R * Math.cos(end);
  const x3 = cx + r * Math.sin(end);
  const y3 = cy - r * Math.cos(end);
  const x4 = cx + r * Math.sin(start);
  const y4 = cy - r * Math.cos(start);
  return `M${x1.toFixed(2)},${y1.toFixed(2)} A${R},${R} 0 ${large} 1 ${x2.toFixed(2)},${y2.toFixed(2)} L${x3.toFixed(2)},${y3.toFixed(2)} A${r},${r} 0 ${large} 0 ${x4.toFixed(2)},${y4.toFixed(2)} Z`;
}

// ── chart primitives ─────────────────────────────────────────────────────

function HBar({
  rows,
  max,
  swatch,
}: {
  rows: Array<{ label: string; value: number; suffix?: string }>;
  max: number;
  swatch?: string;
}) {
  const m = max || Math.max(1, ...rows.map((r) => r.value));
  return (
    <ul className="di-rows">
      {rows.map((r) => (
        <li key={r.label} className="di-row">
          <span className="di-row-label">{r.label}</span>
          <span className="di-row-bar">
            <span
              className="di-row-fill"
              style={{
                width: `${(r.value / m) * 100}%`,
                background: swatch ?? "var(--accent)",
              }}
            />
          </span>
          <span className="di-row-val mono">
            {fmt(r.value)}
            {r.suffix ? <span className="di-row-suffix"> {r.suffix}</span> : null}
          </span>
        </li>
      ))}
    </ul>
  );
}

function Donut({
  parts,
  size = 110,
  thickness = 22,
  centerLabel,
}: {
  parts: Array<{ label: string; value: number; color: string }>;
  size?: number;
  thickness?: number;
  centerLabel?: string;
}) {
  const cx = size / 2;
  const cy = size / 2;
  const R = size / 2 - 2;
  const r = R - thickness;
  const total = parts.reduce((s, p) => s + p.value, 0);
  if (total <= 0) {
    return <div className="caption mono">No data</div>;
  }
  let cursor = 0;
  return (
    <div className="di-donut-wrap">
      <svg
        viewBox={`0 0 ${size} ${size}`}
        width={size}
        height={size}
        aria-hidden="true"
        className="di-donut"
      >
        {parts.map((p, i) => {
          const start = cursor;
          const end = cursor + (p.value / total) * Math.PI * 2;
          cursor = end;
          // Avoid degenerate slice (full-circle case)
          if (end - start >= Math.PI * 2 - 0.001) return null;
          const path = donutSlice(cx, cy, R, r, start, end);
          return (
            <path
              key={`${p.label}-${i}`}
              d={path}
              fill={p.color}
              stroke="var(--bg-1)"
              strokeWidth={1}
            >
              <title>
                {p.label}: {fmt(p.value)} ({pct(p.value, total)})
              </title>
            </path>
          );
        })}
        {centerLabel ? (
          <text
            x={cx}
            y={cy}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={size / 6}
            fontFamily="ui-monospace, monospace"
            fontWeight={600}
            fill="var(--ink)"
          >
            {centerLabel}
          </text>
        ) : null}
      </svg>
      <ul className="di-legend">
        {parts.map((p) => (
          <li key={p.label}>
            <span className="di-legend-swatch" style={{ background: p.color }} />
            <span className="di-legend-label">{p.label}</span>
            <span className="di-legend-val mono">{pct(p.value, total)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Squarified-ish treemap: 1 row, ranked boxes sized by share. Good enough
 * for "the top 6 subdistricts dominate population" story.
 */
function Treemap({
  items,
  swatch,
  height = 90,
}: {
  items: Array<{ label: string; value: number }>;
  swatch?: string;
  height?: number;
}) {
  const total = items.reduce((s, i) => s + i.value, 0);
  if (total <= 0 || items.length === 0) return null;
  const W = 360;
  const H = height;
  // 1-row treemap — widths proportional to value, heights equal.
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="di-treemap" aria-hidden="true">
      {(() => {
        let x = 0;
        return items.map((it) => {
          const w = Math.max(8, (it.value / total) * W);
          const cx = x + w / 2;
          const cy = H / 2;
          const rect = (
            <g key={it.label}>
              <rect
                x={x}
                y={0}
                width={w}
                height={H}
                fill={swatch ?? "var(--accent)"}
                opacity={0.85}
              >
                <title>
                  {it.label}: {fmt(it.value)} ({pct(it.value, total)})
                </title>
              </rect>
              {w >= 60 ? (
                <text
                  x={cx}
                  y={cy - 4}
                  textAnchor="middle"
                  fontSize={10}
                  fontFamily="ui-monospace, monospace"
                  fontWeight={600}
                  fill="var(--on-color)"
                >
                  {pct(it.value, total)}
                </text>
              ) : null}
              {w >= 80 ? (
                <text
                  x={cx}
                  y={cy + 10}
                  textAnchor="middle"
                  fontSize={9}
                  fontFamily="ui-monospace, monospace"
                  fill="var(--on-color)"
                  opacity={0.85}
                >
                  {it.label.length > 14 ? it.label.slice(0, 12) + "…" : it.label}
                </text>
              ) : null}
            </g>
          );
          x += w;
          return rect;
        });
      })()}
    </svg>
  );
}

function Sparkline({
  series,
  width = 360,
  height = 70,
  yLabel,
  swatch = "var(--accent)",
}: {
  series: Array<{ label: string; value: number }>;
  width?: number;
  height?: number;
  yLabel?: string;
  swatch?: string;
}) {
  if (series.length < 2) {
    return <div className="caption mono">Not enough data points for a trend.</div>;
  }
  const PAD = 6;
  const plotW = width - PAD * 2;
  const plotH = height - PAD * 2;
  const vals = series.map((p) => p.value);
  const minV = Math.min(...vals);
  const maxV = Math.max(...vals);
  const range = maxV - minV || 1;
  const xOf = (i: number) => PAD + (i / (series.length - 1)) * plotW;
  const yOf = (v: number) => PAD + plotH - ((v - minV) / range) * plotH;
  const pts = series.map((p, i) => `${xOf(i).toFixed(1)},${yOf(p.value).toFixed(1)}`).join(" ");
  const areaPath = `M${xOf(0).toFixed(1)},${(PAD + plotH).toFixed(1)} L${pts} L${xOf(series.length - 1).toFixed(1)},${(PAD + plotH).toFixed(1)} Z`;
  return (
    <div className="di-spark-wrap">
      <svg viewBox={`0 0 ${width} ${height}`} className="di-spark" aria-hidden="true">
        <path d={areaPath} fill={swatch} opacity={0.18} />
        <polyline
          points={pts}
          fill="none"
          stroke={swatch}
          strokeWidth={2}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {series.map((p, i) => (
          <circle
            key={`${p.label}-${i}`}
            cx={xOf(i)}
            cy={yOf(p.value)}
            r={2.5}
            fill={swatch}
          >
            <title>
              {p.label}: {fmt(p.value)} {yLabel ?? ""}
            </title>
          </circle>
        ))}
      </svg>
      <div className="di-spark-axis mono">
        {series.map((p, i) => (
          <span key={p.label} style={{ left: `${(i / Math.max(1, series.length - 1)) * 100}%` }}>
            {p.label}
          </span>
        ))}
      </div>
    </div>
  );
}

// ── story callouts ───────────────────────────────────────────────────────
//
// Short, headline-y sentences that compare a subdistrict against the district
// average, or call out a dominant category. Each gets a small swatch so the
// connection to the chart above is obvious.

interface Story {
  swatch: string;
  text: string;
}
function pickStories(stats: DistrictStats): Story[] {
  const out: Story[] = [];
  const subs = stats.population.bySubdistrict;
  if (subs.length > 1) {
    const sorted = [...subs].sort((a, b) => b.total - a.total);
    const top = sorted[0];
    const avg = stats.population.total / sorted.length;
    if (top && avg > 0) {
      const ratio = top.total / avg;
      out.push({
        swatch: "var(--accent)",
        text: `${top.subdistrict} leads the district with ${fmt(top.total)} residents — ${ratio.toFixed(1)}× the subdistrict average.`,
      });
    }
  }
  if (stats.businesses.byType.length > 0) {
    const top = stats.businesses.byType[0];
    const total = stats.businesses.total || 1;
    const share = (top.count / total) * 100;
    if (share >= 10) {
      out.push({
        swatch: "var(--cat-amber)",
        text: `${top.type} accounts for ${share.toFixed(0)}% of all ${fmt(total)} registered businesses in Lat Krabang.`,
      });
    }
  }
  if (stats.risks.byType.length > 0) {
    const top = stats.risks.byType[0];
    out.push({
      swatch: "var(--cat-red)",
      text: `Top risk class: ${top.type} — ${fmt(top.count)} reports logged${top.examples[0] ? ` (e.g. ${top.examples[0]})` : ""}.`,
    });
  }
  if (stats.schools.totalStudents > 0 && stats.schools.totalTeachers > 0) {
    const ratio = stats.schools.totalStudents / stats.schools.totalTeachers;
    out.push({
      swatch: "var(--cat-green)",
      text: `${fmt(stats.schools.totalStudents)} students across ${fmt(stats.schools.total)} schools — ${ratio.toFixed(1)} students per teacher on average.`,
    });
  }
  if (stats.religious.byReligion.length > 0) {
    const top = stats.religious.byReligion[0];
    const total = stats.religious.total || 1;
    const share = (top.count / total) * 100;
    out.push({
      swatch: "var(--cat-violet)",
      text: `${top.religion} makes up ${share.toFixed(0)}% of ${fmt(total)} religious sites in the district.`,
    });
  }
  return out;
}

// ── panel ────────────────────────────────────────────────────────────────

export function DistrictInsightsPanel() {
  const stats = useFeed<DistrictStats>(`${API_BASE}/api/district/lat-krabang/stats`, 6 * 60 * 60_000);
  const st = stats.data[0];
  const tier = stats.fallbackTier === "loading" ? "scenario" : stats.fallbackTier;

  const stories = useMemo(() => (st ? pickStories(st) : []), [st]);

  if (!st) {
    return (
      <div className="di-panel">
        <PanelHeader
          title="DISTRICT · INSIGHTS"
          ageMinutes={stats.ageMinutes}
          fallbackTier={tier}
          source="data.go.th"
        />
        <div className="caption mono">Loading data.go.th aggregates…</div>
      </div>
    );
  }

  const genderTotal = st.population.byGender.male + st.population.byGender.female;
  const subByTotal = [...st.population.bySubdistrict].sort((a, b) => b.total - a.total);

  return (
    <div className="di-panel">
      <PanelHeader
        title="DISTRICT · INSIGHTS"
        ageMinutes={stats.ageMinutes}
        fallbackTier={tier}
        source="data.go.th · OSM"
      />

      {/* POPULATION */}
      <section className="di-section">
        <header className="di-h">POPULATION · {fmt(st.population.total)}</header>
        <div className="di-grid-2">
          <Donut
            parts={[
              { label: "Female", value: st.population.byGender.female, color: "var(--cat-pink)" },
              { label: "Male",   value: st.population.byGender.male,   color: "var(--cat-sky)" },
            ]}
            centerLabel={fmt(genderTotal)}
          />
          <div>
            <div className="di-sub">F / M split across the district</div>
            <p className="di-text">
              {pct(st.population.byGender.female, genderTotal)} female, {pct(st.population.byGender.male, genderTotal)} male.
            </p>
            <div className="di-sub" style={{ marginTop: 6 }}>Where they live</div>
            <Treemap
              items={subByTotal.slice(0, 8).map((s) => ({ label: s.subdistrict, value: s.total }))}
              swatch="var(--cat-sky)"
            />
          </div>
        </div>
      </section>

      {/* BUSINESSES */}
      {st.businesses.total > 0 && (
        <section className="di-section">
          <header className="di-h">BUSINESSES · {fmt(st.businesses.total)}</header>
          <HBar
            rows={st.businesses.byType.slice(0, 8).map((b) => ({
              label: b.type,
              value: b.count,
              suffix: pct(b.count, st.businesses.total),
            }))}
            max={Math.max(...st.businesses.byType.map((b) => b.count))}
            swatch="var(--cat-amber)"
          />
        </section>
      )}

      {/* RISKS */}
      {st.risks.total > 0 && (
        <section className="di-section">
          <header className="di-h">RISK POINTS · {fmt(st.risks.total)}</header>
          <HBar
            rows={st.risks.byType.slice(0, 8).map((r) => ({
              label: r.type,
              value: r.count,
              suffix: pct(r.count, st.risks.total),
            }))}
            max={Math.max(...st.risks.byType.map((r) => r.count))}
            swatch="var(--cat-red)"
          />
          {st.risks.byType[0]?.examples?.length ? (
            <div className="di-examples mono">
              e.g. {st.risks.byType.slice(0, 3).flatMap((r) => r.examples).filter(Boolean).slice(0, 3).join(" · ")}
            </div>
          ) : null}
        </section>
      )}

      {/* RELIGIOUS */}
      {st.religious.total > 0 && (
        <section className="di-section">
          <header className="di-h">RELIGIOUS SITES · {fmt(st.religious.total)}</header>
          <HBar
            rows={st.religious.byReligion.slice(0, 6).map((r) => ({
              label: r.religion,
              value: r.count,
              suffix: pct(r.count, st.religious.total),
            }))}
            max={Math.max(...st.religious.byReligion.map((r) => r.count))}
            swatch="var(--cat-violet)"
          />
        </section>
      )}

      {/* SCHOOLS */}
      {st.schools.total > 0 && (
        <section className="di-section">
          <header className="di-h">
            SCHOOLS · {fmt(st.schools.total)} · {fmt(st.schools.totalStudents)} students · {fmt(st.schools.totalTeachers)} teachers
          </header>
          <HBar
            rows={st.schools.byDistrict.slice(0, 6).map((s) => ({
              label: s.district,
              value: s.students,
              suffix: `${fmt(s.teachers)} tch`,
            }))}
            max={Math.max(...st.schools.byDistrict.map((s) => s.students))}
            swatch="var(--cat-green)"
          />
        </section>
      )}

      {/* SOLAR (when present) */}
      {st.solar.length >= 2 && (
        <section className="di-section">
          <header className="di-h">SOLAR PROSUMERS · {fmt(st.solar.reduce((s, p) => s + p.prosumers, 0))}</header>
          <Sparkline
            series={st.solar.map((s) => ({ label: String(s.year), value: s.capacityKw }))}
            yLabel="kWp"
            swatch="var(--cat-yellow)"
          />
        </section>
      )}

      {/* STORY CALLOUTS */}
      {stories.length > 0 && (
        <section className="di-section di-stories">
          <header className="di-h">CROSS-INSIGHTS</header>
          <ul className="di-story-list">
            {stories.map((s, i) => (
              <li key={i} className="di-story">
                <span className="di-story-swatch" style={{ background: s.swatch }} />
                <span>{s.text}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <footer className="caption mono di-foot">
        Sources: data.go.th (BMA เขตลาดกระบัง catalog) · last sync {st.fetchedAt.slice(0, 16).replace("T", " ")}
      </footer>
    </div>
  );
}
